import { t } from '@/features/i18n/utils';
import { useWorkspace } from '@/features/workspaces/helpers';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { WorkspaceBreadcrumbs } from '@/features/workspaces/components/workspace-breadcrumbs';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { H1, H2 } from '@/ui/components/heading';
import { resourceLatestVersionQuery, workspaceQuery } from '@/features/workspaces/queries';
import { WORKSPACE_OVERVIEW_TABS } from '@/features/workspaces/constants';
import { FolderIcon, Clock04Icon, TimeQuarterPassIcon } from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { Assessment } from '@/features/assessment/components/assessment';
import {
  MainContainer,
  MainFrames,
  MainHeader,
  MainNav,
  MainNavButton,
  MainNavCompanyItem,
  MainNavItem,
  MainNavLink,
  MainNavList,
  MainScrollArea,
} from '@/ui/components/main';
import { Header, HeaderChatButton, HeaderBackButton } from '@/ui/components/header';
import { useEffect, useState } from 'react';
import { ActivityPanel } from '@/ui/components/activity-panel';
import { WorkspaceActivity } from '@/features/workspaces/components/workspace-activity';
import { AmbiguitiesFrame } from '@/features/ambiguities/components/ambiguities-frame';
import { ChecklistsFrame } from '@/features/checklists/components/checklists-frame';
import { StandardDeviationsFrame } from '@/features/standard-deviations/components/standard-deviations-frame';
import { ConflictsFrame } from '@/features/conflicts/components/conflicts-frame';
import { ClausesFrame } from '@/features/clauses/components/clauses-frame';
import { SummaryFrame } from '@/features/summary/components/summary-frame';
import { ExtractionsFrame } from '@/features/extractions/components/extractions-frame';
import { PlaybooksFrame } from '@/features/playbooks/components/playbooks-frame';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { noop, useSuspenseQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { versionPanelOpenAtom } from '@/features/workspaces/store';
import { entityDocumentsQuery } from '@/features/documents/queries';
import { chatSidePanelOpenAtom } from '@/features/chat/store';
import { ContractReviewFrame } from '@/features/contract-review/components/contract-review-frame';
import { ScopeSheetFrame } from '@/features/scope-breakdown/components/scope-sheet-frame';
import { scopeAgentStatusQuery } from '@/features/scope-breakdown/queries';
import { TenderRequirements } from '@/features/bid-compliance/components/tender-requirements';
import { AwardCriteria } from '@/features/bids/components/award-criteria';
import { WorkspaceLanguage } from '@/features/workspaces/components/workspace-language';
import { Frame, FramePanel } from '@/ui/components/frame';
import { Tabs, TabsList, TabsPanel, TabsTab } from '@/ui/components/tabs';
import z from 'zod';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/_layout/',
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const workspace = await context.queryClient.query({
      ...workspaceQuery(params.workspaceId),
      staleTime: 'static',
    });
    context.queryClient
      .query(scopeAgentStatusQuery(params.workspaceId, workspace.type))
      .catch(noop);
  },
  validateSearch: z.object({
    tab: z.enum(WORKSPACE_OVERVIEW_TABS).default('brief').catch('brief'),
  }),
});

function RouteComponent() {
  const { projectId, workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const workspace = useWorkspace();
  const home = workspace.type === 'contract' ? 'contractHome' : 'tenderHome';

  const setVersionPanelOpen = useSetAtom(versionPanelOpenAtom);
  const [activityOpen, setActivityOpen] = useState(false);
  const setChatOpen = useSetAtom(chatSidePanelOpenAtom);

  useEffect(() => () => setVersionPanelOpen(false), [setVersionPanelOpen]);

  return (
    <div className="flex grow min-h-0">
      <div className="relative grow flex flex-col min-w-0 min-h-0">
        <Header>
          <HeaderBackButton to="/project/$projectId" params={{ projectId }} />
          <WorkspaceBreadcrumbs />
          <HeaderChatButton />
        </Header>
        <MainScrollArea className="relative">
          <IndexingStatusAlerts resourceId={workspaceId} documentType={workspace.type} />
          <MainHeader className="max-md:hidden">
            <H1>
              <span className="line-clamp-1">
                {workspace.type === 'contract'
                  ? t({ en: 'Contract', nb: 'Kontrakt' })
                  : t({ en: 'Request for proposal (RFP)', nb: 'Forespørsel' })}
              </span>
            </H1>
          </MainHeader>
          <MainNav>
            <MainNavList>
              <MainNavItem className="text-muted items-center whitespace-nowrap px-1.75 h-7 gap-1.5 flex text-sm">
                <WorkspaceLanguage language={workspace.language} className="text-sm gap-1.5" />
              </MainNavItem>
              <MainNavCompanyItem />
              <MainNavItem>
                <MainNavLink
                  to="/project/$projectId/workspaces/$workspaceId/files"
                  params={{ projectId, workspaceId }}
                >
                  <Icon icon={FolderIcon} />
                  {t({ en: 'Files', nb: 'Filer' })}{' '}
                  <SuspenseBoundary>
                    <FilesCount />
                  </SuspenseBoundary>
                </MainNavLink>
              </MainNavItem>
              <MainNavItem>
                <MainNavButton
                  data-selected={activityOpen ? '' : undefined}
                  onClick={() => {
                    setActivityOpen(!activityOpen);
                    if (!activityOpen) setChatOpen(false);
                  }}
                >
                  <Icon icon={Clock04Icon} />
                  {t({ en: 'Activity log', nb: 'Aktivitetslogg' })}
                </MainNavButton>
              </MainNavItem>
              <SuspenseBoundary>
                <VersionHistoryNav />
              </SuspenseBoundary>
            </MainNavList>
          </MainNav>
          <MainContainer>
            {workspace.type === 'tenderPackage' ? (
              <>
                <div className="flex mb-3 justify-between items-center">
                  <H2>{t({ en: 'Tender brief', nb: 'Oversikt' })}</H2>
                </div>
                <Frame>
                  <FramePanel className="p-0">
                    <Tabs
                      className="gap-0"
                      value={search.tab}
                      onValueChange={(value) =>
                        navigate({
                          to: '.',
                          replace: true,
                          search: (prev) => ({ ...prev, tab: value as never }),
                        })
                      }
                    >
                      <TabsList>
                        <TabsTab value="brief">{t({ en: 'Brief', nb: 'Brief' })}</TabsTab>
                        <TabsTab value="awardCriteria">
                          {t({ en: 'Award criteria', nb: 'Tildelingskriterier' })}
                        </TabsTab>
                        <TabsTab value="submissionRequirements">
                          {t({ en: 'Submission requirements', nb: 'Innleveringskrav' })}
                        </TabsTab>
                      </TabsList>
                      <TabsPanel value="brief">
                        <Assessment embedded />
                      </TabsPanel>
                      <TabsPanel value="awardCriteria" className="min-h-40 gap-3 p-4 @3xl:p-5">
                        <AwardCriteria />
                      </TabsPanel>
                      <TabsPanel value="submissionRequirements">
                        <TenderRequirements />
                      </TabsPanel>
                    </Tabs>
                  </FramePanel>
                </Frame>
              </>
            ) : (
              <>
                <H2 className="mb-3">{t({ en: 'Brief', nb: 'Hovedtrekk' })}</H2>
                <Assessment />
              </>
            )}
            <H2 className="mb-3 mt-5">
              {t(
                {
                  contractHome: { en: 'Review', nb: 'Oversikt' },
                  tenderHome: { en: 'Review', nb: 'Gjennomgang' },
                  bids: { en: 'Bid overview', nb: 'Tilbudsoversikt' },
                }[home],
              )}
            </H2>
            <MainFrames>
              <SummaryFrame />
              <ContractReviewFrame />
              <SuspenseBoundary>
                <ScopeSheetFrame />
              </SuspenseBoundary>
            </MainFrames>
            <H2 className="mb-3 mt-5">
              {t(
                {
                  contractHome: { en: 'Findings', nb: 'Gjennomgang' },
                  tenderHome: { en: 'Findings', nb: 'Funn' },
                  bids: { en: 'Tender feedback', nb: 'Anbudstilbakemeldinger' },
                }[home],
              )}
            </H2>
            <MainFrames>
              <PlaybooksFrame />
              <ExtractionsFrame />
              <ClausesFrame />
              <AmbiguitiesFrame />
              <ChecklistsFrame />
              <StandardDeviationsFrame />
              <ConflictsFrame />
            </MainFrames>
          </MainContainer>
        </MainScrollArea>
      </div>
      <ActivityPanel open={activityOpen} onOpenChange={setActivityOpen}>
        <WorkspaceActivity />
      </ActivityPanel>
    </div>
  );
}

function FilesCount() {
  const workspace = useWorkspace();
  const { data: documents } = useSuspenseQuery(entityDocumentsQuery(workspace.id, workspace.type));
  if (!documents.length) return null;
  return `(${documents.length})`;
}

function VersionHistoryNav() {
  const { workspaceId } = Route.useParams();
  const [versionPanelOpen, setVersionPanelOpen] = useAtom(versionPanelOpenAtom);
  const setSidechatOpen = useSetAtom(chatSidePanelOpenAtom);
  const { data: latestVersion } = useSuspenseQuery(resourceLatestVersionQuery(workspaceId));
  if (!latestVersion) return null;
  return (
    <MainNavItem>
      <MainNavButton
        data-selected={versionPanelOpen ? '' : undefined}
        onClick={(e) => {
          e.preventDefault();
          setVersionPanelOpen((prev) => !prev);
          if (!versionPanelOpen) setSidechatOpen(false);
        }}
      >
        <Icon icon={TimeQuarterPassIcon} />
        {t({ en: 'Version history', nb: 'Versjonshistorikk' })} (v
        {latestVersion.version.versionNumber}{' '}
        {latestVersion.version.status === 'active' ? t({ en: 'latest', nb: 'siste' }) : ''})
      </MainNavButton>
    </MainNavItem>
  );
}
